import { Button, Spin, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Images } from '../../config/images';
import useMediaQuery from '../../Hooks/useMediaQuery';
import useSecondaryMarketEligibility from '../../Hooks/useSecondaryMarketEligibility';
import { maxShareCalculatorWithNoOfShare } from '../../utils/maxShareCalculator';
import { toastNotify } from '../../utils/toastNotify';
import './SMHeader.scss';
import { AML_DOCUMENT_PENDING, COMPLETE_VERIFICATION_FIRST, ENTITY_SECONDARY_MARKET_MESSAGE, KYC_NOT_VERIFIED_MESSAGE, WARN_DWOLLA_PENDING, checkOnboardingComplete } from '../../utils/constant';
import SecondaryMarketIdentityUpload from '../SecondaryMarketIdentityUpload/SecondaryMarketIdentityUpload';

const SMHeader = (props) => {
  const isPaused = props.isSecondaryMarketPaused === true;
  // Tooltip message for paused secondary market
  const secondaryPausedTooltip = isPaused
    ? 'The Realbricks secondary market trading is temporarily paused.'
    : '';
  // Helper for tooltip container (fixes tooltip not showing on disabled button)
  const getPopupContainer = (trigger) => trigger.parentNode;
  const { onNotifyClick, setIsSellOpen, propertyDetail } = props;
  const screenSM = useMediaQuery('(min-width: 640px)');
  const {
    loading,
    data: {
      data: {
        images = {},
        name = '',
        city = '',
        state = '',
        propertyTypeName = '',
        tag = '',
        propertyId = '',
        isSecondary = false,
        totalShare = 0,
        requestedShares = 0,
        userPurchaseDetail: { noOfShares = 0 } = {},
        isSellOrdersTradable = false,
      } = {},
    } = {},
  } = useSelector((state) => state.propertyDetails);
  const userData = useSelector((state) => state.userData);
  const navigate = useNavigate();

  const notifyLoading = useSelector((state) => state.notifyMeSlice.loading);

  const isBuyDisabled = useMemo(() => {
    const sharesRemainingToPurchase = (Number(totalShare) * 9.8) / 100 - Number(noOfShares);
    return Number(sharesRemainingToPurchase) < 1;
  }, [noOfShares, totalShare]);

  const { isSellEligible, sellIneligibleMsg, isEntityRestricted } = useSecondaryMarketEligibility();
  const [showIdentityModal, setShowIdentityModal] = useState(false);

  const handleBuyClick = () => {
    if (isEntityRestricted) {
      toastNotify('error', ENTITY_SECONDARY_MARKET_MESSAGE);
      return;
    }
    setIsSellOpen('1');
    Object.keys(userData?.userData).length > 0
      ? userData?.userData?.isKYCVerified && userData?.userData?.isDwollaVerified
        ? !userData?.userData?.isAMLApproved && userData?.userData?.isDocRequiredForAml
          ? toastNotify('error', <AML_DOCUMENT_PENDING onOpenModal={() => setShowIdentityModal(true)} />)
          : props.clickAction('StepOne')
        : !userData?.userData?.isKYCVerified
        ? checkOnboardingComplete(userData?.userData, userData?.counter)
          ? toastNotify('error', KYC_NOT_VERIFIED_MESSAGE)
          : toastNotify('error', <COMPLETE_VERIFICATION_FIRST navigate={navigate}/>)
        : toastNotify('error', WARN_DWOLLA_PENDING)
      : navigate('/login');
  };

  const handleSellClick = () => {
    if (isEntityRestricted) {
      toastNotify('error', ENTITY_SECONDARY_MARKET_MESSAGE);
      return;
    }
    setIsSellOpen('2');
    Object.keys(userData?.userData).length > 0
      ? userData?.userData?.isKYCVerified && userData?.userData?.isDwollaVerified
        ? !userData?.userData?.isAMLApproved && userData?.userData?.isDocRequiredForAml
          ? toastNotify('error', <AML_DOCUMENT_PENDING onOpenModal={() => setShowIdentityModal(true)} />)
          : props.clickAction('StepOne')
        : !userData?.userData?.isKYCVerified
        ? checkOnboardingComplete(userData?.userData, userData?.counter)
          ? toastNotify('error', KYC_NOT_VERIFIED_MESSAGE)
          : toastNotify('error', <COMPLETE_VERIFICATION_FIRST navigate={navigate}/>)
        : toastNotify('error', WARN_DWOLLA_PENDING)
      : navigate('/login');
  };

  // (already declared above, do not redeclare)
  return (
    <Spin spinning={loading}>
      <div className='smheader'>
        <div className='smheader__top top'>
          <div className='top__image'>
            <LazyLoadImage
              onError={(element) => {
                element.target.src = Images.noPropertyFoundImage;
              }}
              src={images?.[images?.primaryImage]}
              alt={name}
            />
          </div>
          <div className='top__data'>
            <h3>{name}</h3>
            <span className='text-taupe'>
              {city}<span className='mr-4 ml-4 text-taupe-60'>•</span>{state} <span className='mr-4 ml-4 text-taupe-60'>•</span> {propertyTypeName}
            </span>
          </div>
          {!!screenSM && (
            <>
              {isEntityRestricted ? null : tag !== 'Coming Soon' ? (
                <div className='top__actions'>
                  <span>
                    <Tooltip
                      title={
                        Number(propertyDetail?.userPurchaseDetail?.noOfShares) <= 0
                          ? ''
                          : !isSellEligible
                          ? sellIneligibleMsg
                          : !isSellOrdersTradable
                          ? 'You cannot create this sell order because some or all of your shares are already placed in open sell orders.'
                          : secondaryPausedTooltip
                      }
                      mouseEnterDelay={0.1}
                      getPopupContainer={getPopupContainer}
                    >
                      <span>
                        <Button
                          onClick={handleSellClick}
                          disabled={isPaused || Number(propertyDetail?.userPurchaseDetail?.noOfShares) <= 0 || tag==='Funded' || !isSellOrdersTradable || !isSellEligible}
                        >
                          Sell
                        </Button>
                      </span>
                    </Tooltip>
                  </span>
                  <span>
                    <Tooltip
                      title={secondaryPausedTooltip}
                      mouseEnterDelay={0.1}
                      getPopupContainer={getPopupContainer}
                    >
                      <span>
                        <Button
                          type='primary'
                          onClick={handleBuyClick}
                          disabled={
                            isPaused ||
                            isBuyDisabled ||
                            maxShareCalculatorWithNoOfShare(totalShare, requestedShares, propertyDetail?.userPurchaseDetail?.noOfShares) <= 0||
                            tag==='Funded'
                          }
                        >
                          Buy
                        </Button>
                      </span>
                    </Tooltip>
                  </span>
                </div>
              ) : (
                <div className='top__actions'>
                  <Button onClick={() => onNotifyClick({ id: propertyId, isSecondary: isSecondary })} loading={notifyLoading}>
                    Notify me
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
        {!screenSM && (
          <>
            {isEntityRestricted ? null : tag !== 'Coming Soon' ? (
              <span>
                <Tooltip
                  title={secondaryPausedTooltip}
                  mouseEnterDelay={0.1}
                  getPopupContainer={getPopupContainer}
                >
                  <span>
                    <Button
                      className='w-100 mt-38'
                      type='primary'
                      onClick={() => {
                        Object.keys(userData?.userData).length > 0
                          ? userData?.userData?.isKYCVerified && userData?.userData?.isDwollaVerified
                            ? !userData?.userData?.isAMLApproved && userData?.userData?.isDocRequiredForAml
                              ? toastNotify('error', <AML_DOCUMENT_PENDING onOpenModal={() => setShowIdentityModal(true)} />)
                              : props.clickAction('StepOne')
                            : !userData?.userData?.isKYCVerified
                            ? checkOnboardingComplete(userData?.userData, userData?.counter)
                              ? toastNotify('error', KYC_NOT_VERIFIED_MESSAGE)
                              : toastNotify('error', <COMPLETE_VERIFICATION_FIRST navigate={navigate}/>)
                            : toastNotify('error', WARN_DWOLLA_PENDING)
                          : navigate('/login');
                      }}
                      disabled={isPaused || tag==='Funded'}
                    >
                      Trade
                    </Button>
                  </span>
                </Tooltip>
              </span>
            ) : (
              <Button className='w-100 mt-38' onClick={() => onNotifyClick({ id: propertyId, isSecondary: isSecondary })} loading={notifyLoading}>
                Notify me
              </Button>
            )}
          </>
        )}
      </div>
      <SecondaryMarketIdentityUpload
        visible={showIdentityModal}
        onClose={() => setShowIdentityModal(false)}
        onSuccess={() => setShowIdentityModal(false)}
      />
    </Spin>
  );
};

export default SMHeader;
